import { task, timeout } from 'ember-concurrency';
import { isThenable } from 'volta/utils/object-utils';

import { debug } from '@ember/debug';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import BvBtnContent from './content';
import { LinkTo } from '@ember/routing';

import classNames from 'volta/helpers/class-names';
import or from 'ember-truth-helpers/helpers/or';
import and from 'ember-truth-helpers/helpers/and';
import not from 'ember-truth-helpers/helpers/not';
import orElse from 'volta/helpers/or-else';
import { array } from '@ember/helper';
import { hash } from '@ember/helper';
import { fn } from '@ember/helper';
import optional from '@nullvoxpopuli/ember-composable-helpers/helpers/optional';
import eq from 'lodash/eq';

import type { BvBtnContentArgs } from './content';
import type { TSize } from 'volta/utils/style-utils';
import type { TText } from 'volta/utils/text-utils';
import type { ContentValue } from '@glint/template';
import { on } from '@ember/modifier';

export type TButtonSize = Extract<TSize, 'xs' | 'sm'>;

const Sizes: Record<TButtonSize, TButtonSize> = {
  sm: 'sm',
  xs: 'xs'
};

export type IBtnColor = 'primary' | 'danger' | 'hud';

const Colors: Record<IBtnColor, IBtnColor> = {
  primary: 'primary',
  danger: 'danger',
  hud: 'hud'
};

export type IAlignment = 'left' | 'right';

const Alignments: Record<IAlignment, IAlignment> = {
  left: 'left',
  right: 'right'
};

export interface BvBtnArgs
  extends Pick<
    BvBtnContentArgs,
    | 'disclosure'
    | 'iconLeft'
    | 'iconLeftColor'
    | 'iconRight'
    | 'iconRightColor'
    | 'tooltip'
    | 'tooltipText'
    | 'tooltipSide'
    | 'size'
    | 'pressed'
  > {
  accessibilityLabel?: string;
  active?: boolean;
  alignText?: IAlignment;
  class?: string;
  color?: IBtnColor;
  content?: ContentValue; // deprecated
  disabled?: boolean;
  fullWidth?: boolean;
  isLoading?: boolean;
  loadingProgress?: number;
  outline?: boolean;
  plain?: boolean;
  rounded?: boolean;
  route?: string;
  routeModel?: unknown;
  routeModels?: unknown[];
  routeQuery?: Record<string, unknown>;
  submit?: boolean;
  subtle?: boolean;
  testName?: string;
  text?: TText;
  type?: 'submit' | 'button';
  onClick?: (event: Event) => any;
  onFocus?: (event: Event) => void;
  onBlur?: (event: Event) => void;
}

export interface BvBtnSignature {
  Args: BvBtnArgs;
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement | HTMLElement;
}

const BvBtnColors = new Set([Colors.primary, Colors.danger, Colors.hud]);
const BvBtnSizes = new Set([Sizes.sm, Sizes.xs]);
const BvBtnAlignments = new Set([Alignments.left, Alignments.right]);

/**
 * Timeout used in async action to simulated a minimum loading time in milliseconds
 */
const DefaultTaskTimeout: number = 300;

export default class BvBtnComponent extends Component<BvBtnSignature> {
  promise?: Promise<any>;

  /**
   * Button color class name
   */
  get colorClassName() {
    const { color } = this.args;
    return color && BvBtnColors.has(color) ? `bv-btn--${color}` : undefined;
  }

  /**
   * Button size class modifier
   */
  get sizeClassName() {
    const { size } = this.args;
    return size && BvBtnSizes.has(size) ? `bv-btn--${size}` : undefined;
  }

  /**
   * BvBtn alignment class modifier
   */
  get alignClassName() {
    const { alignText } = this.args;
    return alignText && BvBtnAlignments.has(alignText as IAlignment)
      ? `bv-btn--align-${alignText}`
      : undefined;
  }

  /**
   * BvBtn has only one icon defined in its args
   * true if one icon only is defined and no text
   */
  get hasOnlyOneIcon() {
    const { iconLeft, iconRight, text, disclosure } = this.args;
    return (
      !text &&
      ((Boolean(iconLeft) && !iconRight && !disclosure) ||
        (!iconLeft && (Boolean(iconRight) || Boolean(disclosure))))
    );
  }

  @action
  handleRouteClick(event: MouseEvent): boolean | { event: MouseEvent } {
    if (this.args.disabled) {
      return false;
    }
    event.stopPropagation();
    return this.handleClick(event);
  }

  /**
   * BvBtn click event handler. If the passed onClick function returns a promise
   * the asyncAction will be performed
   * @param event HTML element click event
   * @return {boolean|*}
   */
  @action
  handleClick(event: MouseEvent): boolean | { event: MouseEvent } {
    const { submit, onClick, disabled } = this.args;

    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }

    if (submit) {
      return { event };
    }

    const maybePromise: Promise<any> | undefined = onClick?.(event);

    if (maybePromise && isThenable(maybePromise)) {
      this.promise = maybePromise;
      this.asyncAction.perform(maybePromise);
    }
    return false;
  }

  /**
   * Asynchronous action performed when the passed onClick argument returns a promise
   * @param promise A promise returned by an async function passed in the onClick argument
   */
  asyncAction = task(async (promise?: Promise<any>) => {
    try {
      if (!promise) {
        return;
      }
      await timeout(DefaultTaskTimeout);
      return await promise;
    } catch (error) {
      debug(error as string);
    }
  });

  <template>
    {{#let (or @isLoading this.asyncAction.isRunning) as |isBusy|}}
      {{#let
        (or @disabled isBusy)
        (classNames
          'bv-btn'
          this.colorClassName
          this.sizeClassName
          this.alignClassName
          (if (or @active @pressed) 'bv-btn--pressed')
          (if @outline 'bv-btn--outline')
          (if @rounded 'bv-btn--rounded')
          (if (and @subtle (not @plain)) 'bv-btn--subtle')
          (if (and @plain (not @subtle)) 'bv-btn--plain')
          (if @fullWidth 'bv-btn--full-width')
          (if (and this.hasOnlyOneIcon (not (has-block))) 'bv-btn--icon')
          (if isBusy 'bv-btn--loading')
          (if (or @disabled isBusy) 'bv-btn--disabled')
          @class
        )
        (component
          BvBtnContent
          text=@text
          size=@size
          isBusy=isBusy
          pressed=@pressed
          tooltip=@tooltip
          tooltipText=@tooltipText
          tooltipSide=@tooltipSide
          asyncAction=this.asyncAction
          iconLeft=@iconLeft
          iconLeftColor=@iconLeftColor
          iconRight=@iconRight
          iconRightColor=@iconRightColor
          disclosure=@disclosure
          loadingProgress=@loadingProgress
        )
        as |isDisabled classNames ContentComponent|
      }}
        {{#if @route}}
          <LinkTo
            @route={{@route}}
            @models={{orElse @routeModels (if @routeModel (array @routeModel) (array))}}
            @query={{orElse @routeQuery (hash)}}
            @activeClass='bv-btn--pressed'
            @disabled={{isDisabled}}
            class={{classNames}}
            aria-label={{@accessibilityLabel}}
            data-test-bv-btn-name={{@testName}}
            ...attributes
            {{on 'click' this.handleRouteClick}}
            {{on 'focus' (fn (optional @onFocus))}}
            {{on 'blur' (fn (optional @onBlur))}}
          >
            {{#if (has-block)}}
              <ContentComponent>
                {{yield}}
              </ContentComponent>
            {{else}}
              <ContentComponent />
            {{/if}}
          </LinkTo>
        {{else}}
          <button
            class={{classNames}}
            disabled={{isDisabled}}
            type={{if (eq @type 'submit') @type 'button'}}
            aria-busy={{isBusy}}
            aria-label={{orElse @accessibilityLabel @tooltipText}}
            data-test-bv-btn-name={{@testName}}
            ...attributes
            {{on 'click' this.handleClick}}
            {{on 'focus' (fn (optional @onFocus))}}
            {{on 'blur' (fn (optional @onBlur))}}
          >
            {{#if (has-block)}}
              <ContentComponent>
                {{yield}}
              </ContentComponent>
            {{else}}
              <ContentComponent />
            {{/if}}
          </button>
        {{/if}}
      {{/let}}
    {{/let}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvBtn: typeof BvBtnComponent;
    'bv-btn': typeof BvBtnComponent;
  }
}
